import React, { useState, useEffect } from 'react';
import { branchesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  MapPin, Plus, Power, Users, Buildings, PencilSimple, UserPlus, UserMinus,
  CaretDown, CaretUp,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const ROLE_LABELS = {
  hospital_admin: 'Hospital Admin', claim_manager: 'Claim Manager',
  billing_executive: 'Billing Executive', tpa_desk: 'TPA Desk',
  ar_executive: 'AR Executive', finance_manager: 'Finance Manager',
  management_viewer: 'Management Viewer', reception_user: 'Reception User',
};

const BranchesPage = () => {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', code: '', address: '', city: '', state: '', pincode: '', phone: '', email: '', head_name: ''
  });

  // Branch-user mapping
  const [expandedBranch, setExpandedBranch] = useState(null);
  const [branchUsers, setBranchUsers] = useState({});
  const [assignDialog, setAssignDialog] = useState(null);
  const [unassignedUsers, setUnassignedUsers] = useState([]);

  useEffect(() => { loadBranches(); }, []);

  const loadBranches = async () => {
    try {
      const res = await branchesApi.list();
      setBranches(res.data);
    } catch (err) { toast.error('Failed to load branches'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.name) { toast.error('Branch name required'); return; }
    try {
      await branchesApi.create(form);
      toast.success(`Branch "${form.name}" created`);
      setDialogOpen(false);
      setForm({ name: '', code: '', address: '', city: '', state: '', pincode: '', phone: '', email: '', head_name: '' });
      loadBranches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create branch');
    }
  };

  const handleToggle = async (id, name, active) => {
    try {
      await branchesApi.toggle(id);
      toast.success(`${name} ${active ? 'deactivated' : 'activated'}`);
      loadBranches();
    } catch (err) { toast.error('Failed to toggle branch'); }
  };

  const toggleExpandBranch = async (branchId) => {
    if (expandedBranch === branchId) {
      setExpandedBranch(null);
      return;
    }
    setExpandedBranch(branchId);
    try {
      const res = await branchesApi.getUsers(branchId);
      setBranchUsers(prev => ({ ...prev, [branchId]: res.data }));
    } catch (e) { console.error(e); }
  };

  const openAssignDialog = async (branchId) => {
    setAssignDialog(branchId);
    try {
      const res = await branchesApi.getUnassigned();
      setUnassignedUsers(res.data);
    } catch (e) { toast.error('Failed to load unassigned users'); }
  };

  const assignUser = async (branchId, userId, userName) => {
    try {
      await branchesApi.assignUser(branchId, userId);
      toast.success(`${userName} assigned to branch`);
      // Refresh
      const [brRes, unRes] = await Promise.all([branchesApi.getUsers(branchId), branchesApi.getUnassigned()]);
      setBranchUsers(prev => ({ ...prev, [branchId]: brRes.data }));
      setUnassignedUsers(unRes.data);
      loadBranches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign'); }
  };

  const unassignUser = async (branchId, userId, userName) => {
    try {
      await branchesApi.unassignUser(branchId, userId);
      toast.success(`${userName} removed from branch`);
      const res = await branchesApi.getUsers(branchId);
      setBranchUsers(prev => ({ ...prev, [branchId]: res.data }));
      loadBranches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to unassign'); }
  };

  const isAdmin = user?.role === 'hospital_admin' || user?.roles?.includes('hospital_admin');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div></div>;

  return (
    <div className="space-y-6" data-testid="branches-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="branches-title">Branches</h1>
          <p className="text-sm text-slate-500 mt-1">Manage branches and assign team members</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white shadow-lg shadow-cyan-500/25 gap-2" data-testid="add-branch-btn">
              <Plus className="h-4 w-4" /> Add Branch
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Add New Branch</DialogTitle>
              <DialogDescription>Create a new branch for your hospital</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Branch Name *</Label><Input placeholder="Main Branch" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="branch-name-input" /></div>
                <div><Label>Branch Code</Label><Input placeholder="Auto-generated" value={form.code} onChange={e => setForm(f => ({...f, code: e.target.value}))} /></div>
              </div>
              <div><Label>Address</Label><Input placeholder="Full address" value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>City</Label><Input value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} /></div>
                <div><Label>State</Label><Input value={form.state} onChange={e => setForm(f => ({...f, state: e.target.value}))} /></div>
                <div><Label>Pincode</Label><Input value={form.pincode} onChange={e => setForm(f => ({...f, pincode: e.target.value}))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} /></div>
              </div>
              <div><Label>Branch Head</Label><Input placeholder="Dr. Name" value={form.head_name} onChange={e => setForm(f => ({...f, head_name: e.target.value}))} /></div>
              <Button className="w-full bg-gradient-to-r from-cyan-500 to-teal-600 text-white" onClick={handleCreate} data-testid="submit-branch-btn">Create Branch</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border border-slate-200 overflow-hidden">
          <CardContent className="pt-5 pb-4 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Branches</p>
                <p className="text-3xl font-bold text-slate-800 mt-1 font-['Outfit']">{branches.length}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-cyan-100 flex items-center justify-center"><Buildings className="h-6 w-6 text-cyan-600" weight="duotone" /></div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 to-teal-500"></div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 overflow-hidden">
          <CardContent className="pt-5 pb-4 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Active Branches</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1 font-['Outfit']">{branches.filter(b => b.is_active).length}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center"><Power className="h-6 w-6 text-emerald-600" weight="duotone" /></div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-green-500"></div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 overflow-hidden">
          <CardContent className="pt-5 pb-4 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Assigned Staff</p>
                <p className="text-3xl font-bold text-violet-600 mt-1 font-['Outfit']">{branches.reduce((sum, b) => sum + (b.user_count || 0), 0)}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-violet-100 flex items-center justify-center"><Users className="h-6 w-6 text-violet-600" weight="duotone" /></div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-400 to-purple-500"></div>
          </CardContent>
        </Card>
      </div>

      {/* Branches Table with Expandable User Mapping */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="branches-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Branch</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Code</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Location</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Head</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Users</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Claims</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {branches.map((b) => (
                  <React.Fragment key={b.id}>
                    <TableRow className={`hover:bg-cyan-50/50 transition-colors cursor-pointer ${expandedBranch === b.id ? 'bg-cyan-50/30' : ''}`}
                      onClick={() => toggleExpandBranch(b.id)} data-testid={`branch-row-${b.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {expandedBranch === b.id ? <CaretUp className="h-3.5 w-3.5 text-cyan-500" /> : <CaretDown className="h-3.5 w-3.5 text-slate-400" />}
                          <span className="font-semibold text-slate-800">{b.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-cyan-700 font-mono">{b.code || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{[b.city, b.state].filter(Boolean).join(', ') || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{b.head_name || '-'}</TableCell>
                      <TableCell className="text-center"><Badge className="bg-violet-100 text-violet-700 border-0">{b.user_count || 0}</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-sky-100 text-sky-700 border-0">{b.claim_count || 0}</Badge></TableCell>
                      <TableCell>
                        <Badge className={`${b.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} border-0 text-xs`}>
                          {b.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          {isAdmin && (
                            <Button variant="ghost" size="sm" className="hover:bg-cyan-50 text-cyan-600 h-7 w-7 p-0"
                              onClick={(e) => { e.stopPropagation(); openAssignDialog(b.id); }}
                              data-testid={`assign-user-${b.id}`} title="Assign User">
                              <UserPlus className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleToggle(b.id, b.name, b.is_active); }}
                            className={`h-7 w-7 p-0 ${b.is_active ? 'hover:bg-red-50 text-red-500' : 'hover:bg-emerald-50 text-emerald-600'}`}
                            data-testid={`toggle-branch-${b.id}`}>
                            <Power className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Expanded User List */}
                    {expandedBranch === b.id && (
                      <TableRow>
                        <TableCell colSpan={8} className="bg-slate-50/50 p-0">
                          <div className="px-6 py-3" data-testid={`branch-users-${b.id}`}>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                                <Users className="h-3.5 w-3.5 text-violet-500" /> Team Members in {b.name}
                              </p>
                              {isAdmin && (
                                <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 border-cyan-200 text-cyan-600"
                                  onClick={() => openAssignDialog(b.id)}>
                                  <UserPlus className="h-3 w-3" /> Assign User
                                </Button>
                              )}
                            </div>
                            {(branchUsers[b.id] || []).length > 0 ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {(branchUsers[b.id] || []).map(u => (
                                  <div key={u.id} className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 p-2">
                                    <div className="h-7 w-7 rounded-full bg-gradient-to-br from-sky-400 to-violet-400 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                                      {u.name?.charAt(0)?.toUpperCase() || 'U'}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-slate-800 truncate">{u.name}</p>
                                      <p className="text-[9px] text-slate-400 truncate">{(u.roles || []).map(r => ROLE_LABELS[r] || r).join(', ')}</p>
                                    </div>
                                    {isAdmin && (
                                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 hover:bg-red-50 text-red-400 shrink-0"
                                        onClick={() => unassignUser(b.id, u.id, u.name)}
                                        data-testid={`unassign-${u.id}`} title="Remove from branch">
                                        <UserMinus className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-slate-400 italic py-2">No users assigned to this branch</p>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                {branches.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500">
                    <MapPin className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">No branches created yet</p>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Assign User Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-cyan-500" /> Assign User to Branch
            </DialogTitle>
            <DialogDescription>Select team members to assign to this branch</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2" data-testid="assign-user-dialog">
            {unassignedUsers.length > 0 ? unassignedUsers.map(u => (
              <div key={u.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-200 hover:border-cyan-300 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-sky-400 to-violet-400 flex items-center justify-center text-white text-xs font-bold">
                    {u.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{u.name}</p>
                    <p className="text-[10px] text-slate-400">{u.email} &middot; {(u.roles || []).map(r => ROLE_LABELS[r] || r).join(', ')}</p>
                  </div>
                </div>
                <Button size="sm" className="h-7 text-xs gap-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                  onClick={() => assignUser(assignDialog, u.id, u.name)} data-testid={`assign-btn-${u.id}`}>
                  <UserPlus className="h-3 w-3" /> Assign
                </Button>
              </div>
            )) : (
              <div className="text-center py-8 text-slate-400">
                <Users className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">All users are already assigned to branches</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchesPage;
